import { FetchAccountsReceivableQuery } from '../../generated'
import { calculateClientBillData } from './billing'

type AREntry = FetchAccountsReceivableQuery['fetchAccountsReceivable']['arEntries'][number]
type ClientRole = NonNullable<AREntry['caseExternalUserToExternalUserRoles']>[number]
type AccessToken = NonNullable<NonNullable<AREntry['bill']>['accessTokens']>[number] | undefined


const getClientBillData = (entry: AREntry, clientRole: ClientRole, accessToken: AccessToken, clientShare: number) => {
  const { bill, case: litigation, paymentAllocations } = entry || {}
  const clientId = clientRole?.externalUserToExternalUserRole?.externalUser?.id

  const clientInterest = entry.clientsInterest?.find(interest => interest.clientId === clientId)

  const clientWrittenOff = (accessToken?.writtenOffPrincipal || 0) + (accessToken?.writtenOffInterest || 0)
  const billWrittenOff = (bill?.writtenOffPrincipal || 0) + (bill?.writtenOffInterest || 0)

  const { paymentAllocations: clientPaymentAllocations, payableAmount, writtenOff } = calculateClientBillData({
    paymentAllocations: paymentAllocations || [],
    bill: {
      splitBill: litigation?.billing?.splitBillEnabled,
      amount: entry.total,
      status: bill?.status,
      discount: bill?.discount || 0,
      interestAccrued: clientInterest?.interestAccrued || 0,
      writtenOff: billWrittenOff
    },
    client: {
      id: clientId,
      share: clientShare,
      writtenOff: clientWrittenOff
    }
  })

  const sortedPaymentAllocations = [...clientPaymentAllocations].sort(
    (a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime()
  )
  const recentPaymentAllocation = sortedPaymentAllocations[0]

  const total = clientShare + (clientInterest?.interestAccrued || 0)
  const balance = payableAmount
  const paid = total - balance - (writtenOff || 0)

  return {
    bill,
    writtenOff,
    case: litigation,
    recentPaymentAllocation,
    total,
    balance,
    paid: Math.max(0, paid),
    client: { ...clientRole.externalUserToExternalUserRole?.externalUser, caseRole: clientRole.caseRole }
  }
}

export const splitBillsByClient = (arEntries: AREntry[]) => {
  return arEntries
    .map(entry => {
      const { bill, case: litigation, caseExternalUserToExternalUserRoles } = entry || {}

      if (litigation?.billing?.splitBillEnabled) {
        const clientRoles = caseExternalUserToExternalUserRoles?.filter(role => !!role.splitBillShare)!

        return clientRoles.map(clientRole => {
          const clientId = clientRole?.externalUserToExternalUserRole?.externalUser?.id
          const accessToken = bill?.accessTokens?.find(token => token.client?.id === clientId)
          const totalClientShare = accessToken?.amount || 0

          return getClientBillData(entry, clientRole, accessToken, totalClientShare)
        })
      }

      const accessToken = bill?.accessTokens?.[0]
      const clientId = accessToken?.client?.id
      const primaryClient = caseExternalUserToExternalUserRoles?.find(
        role => role.externalUserToExternalUserRole?.externalUser?.id === clientId
      )

      if (!primaryClient) {
        return []
      }

      return getClientBillData(entry, primaryClient, accessToken, entry.total)
    })
    .flat()
    .filter(entry => !!entry.balance && !!entry.client?.id)
}
